import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useModalTab } from '../search/WebshopConfigSearch';
import { webshopModalSizing } from '../_webshopUi';
import {
    Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, InputAdornment, List, ListItemButton, ListItemIcon, ListItemText,
    MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import GridViewIcon from '@mui/icons-material/GridView';
import TranslateIcon from '@mui/icons-material/Translate';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { ConfigCard } from '../style/cards';
import UnlayerEditor from '../../../components/UnlayerEditor';
import SftGlobalData from '../../../library/SftGlobalData';

export const CADEAUBON_CONFIG_DEFAULT = {
    availability: {
        showInNavigation: false,
        allowPhysical: true,
        allowDigital: true,
    },
    amounts: {
        presets: [10, 25, 50, 100],
        allowCustom: true,
        customMin: 10,
        customMax: 250,
    },
    discounts: {
        countTowardTotals: false,
        applyToVoucherOnly: false,
        applyToMixedOrders: false,
    },
    shipping: {
        cost: 0,
        freeFrom: 999,
    },
    personalization: {
        // Mirrors the old admin: two voucher-type selects + one toggle.
        infoOnType: 'BOTH',           // CHEQUES_ALLOW_INFO_ON_TYPE    — NONE|PHYSICAL|DIGITAL|BOTH
        sendDigitalDirectly: true,    // CHEQUES_ALLOW_SEND_TO_RECEIVER
        messageOnType: 'BOTH',        // CHEQUES_ALLOW_MESSAGE_ON_TYPE — NONE|PHYSICAL|DIGITAL|BOTH
    },
};

// FLD↔PLC review (2026-06-04) — RESOLVED. The old "standard voucher labels"
// (FLD_* keys) were frontend-mockup keys that never existed in any backend.
// Every one of them maps 1-to-1 onto a seeded `cheques_placeholders` row
// (mig117 + mig127) that is ALREADY editable in the Translations table above:
//   FLD_GIFT_VOUCHER → PLC_GIFT_VOUCHER     FLD_VALUE → PLC_VALUE
//   FLD_VALID_TO_INFO → PLC_VALID_UNTIL     FLD_CHEQUE_NUMBER → PLC_CHEQUE_NUMBER
//   FLD_CHEQUE_CHECKSUM → PLC_CHEQUE_CHECKSUM   FLD_TERM → PLC_CHEQUE_CONDITIONS
//   FLD_COMMERCIAL_DISCOUNT / _CUSTOMER_DISCOUNT / _CUSTOMER_DISCOUNT_COUPON →
//   the matching PLC_* rows.
// The read-only reference table was pure duplication and is gone. What remains
// is this guard list: the seeded system placeholders are referenced as
// {{PLC_*}} tokens by the default layout templates (default_*.html), so
// deleting them would leave unreplaced tokens on customers' vouchers —
// editing stays allowed, deleting is blocked in the UI.
const SYSTEM_PLACEHOLDER_KEYS = [
    'PLC_GIFT_VOUCHER', 'PLC_VALUE', 'PLC_VALID_UNTIL', 'PLC_CHEQUE_NUMBER',
    'PLC_CHEQUE_CHECKSUM', 'PLC_CHEQUE_CONDITIONS',
    'PLC_COMMERCIAL_DISCOUNT', 'PLC_CUSTOMER_DISCOUNT',
    'PLC_CUSTOMER_DISCOUNT_COUPON', 'PLC_DEPOSIT', 'PLC_DISCOUNT_TYPE', 'PLC_VOUCHER',
];

const CadeaubonTab = ({ theme, t, config: configProp, onConfigChange, placeholders: placeholdersProp, onPlaceholdersChange, layouts: layoutsProp, onLayoutsChange, login, api, selectedWebshop }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);

    // 0 = Settings, 1 = Voucher layouts, 2 = Translations
    const [voucherTab, setVoucherTab] = useState(0);
    // Let the global search jump straight to a sub-tab (settings/layouts/trans).
    useModalTab('cadeaubon', setVoucherTab);

    // SETTINGS — controlled by the parent (CHEQUES_* settings) when wired.
    const controlled = configProp != null && typeof onConfigChange === 'function';
    const [localConfig, setLocalConfig] = useState(CADEAUBON_CONFIG_DEFAULT);
    const config = controlled ? configProp : localConfig;
    const setConfig = controlled ? onConfigChange : setLocalConfig;
    const setSection = (sec) => (patch) => setConfig(prev => ({ ...prev, [sec]: { ...prev[sec], ...patch } }));

    const setAvailability    = setSection('availability');
    const setAmounts         = setSection('amounts');
    const setDiscounts       = setSection('discounts');
    // "Send directly to receiver" only makes sense when sender/receiver info is
    // collected on a digital voucher — so it's locked off for physical-only
    // shops or when info is None/Physical (same rule as the old admin).
    const physicalOnly = !!config.availability.allowPhysical && !config.availability.allowDigital;
    const sendToReceiverDisabled = physicalOnly
        || config.personalization.infoOnType === 'NONE'
        || config.personalization.infoOnType === 'PHYSICAL';
    const setShipping        = setSection('shipping');
    const setPersonalization = setSection('personalization');

    const addPreset = (val) => {
        const n = parseInt(val);
        if (!Number.isFinite(n) || n <= 0) return;
        if (config.amounts.presets.includes(n)) return;
        setAmounts({ presets: [...config.amounts.presets, n].sort((a, b) => a - b) });
    };
    const removePreset = (val) => {
        setAmounts({ presets: config.amounts.presets.filter(p => p !== val) });
    };

    // VOUCHER LAYOUTS — controlled by the parent (cheques_layouts CRUD + image
    // uploads + Unlayer design). Flat list; grouped here into physical / digital
    // / system. System defaults (cheque_type set) can be duplicated, not deleted.
    const layouts = Array.isArray(layoutsProp) ? layoutsProp : [];
    const setLayouts = (updater) => {
        if (typeof onLayoutsChange !== 'function') return;
        onLayoutsChange(typeof updater === 'function' ? updater(layouts) : updater);
    };
    const physicalLayouts = layouts.filter(l => l.type === 'PHYSICAL' && !l.isSystem);
    const digitalLayouts  = layouts.filter(l => l.type === 'DIGITAL'  && !l.isSystem);
    const systemDefaults  = layouts.filter(l => l.isSystem);
    const blankLayout = (type, extra = {}) => ({
        id: `new_${Date.now()}_${layouts.length}`, serverId: null, type,
        description: tr('cfg_voucher_layouts_new_name', 'New layout'),
        visible: false, isSystem: false, chequeType: null,
        image: null, layoutImage: null, imageFile: null, layoutImageFile: null,
        designDirty: false, design: null, html: null, designLoaded: false, copyFrom: null,
        ...extra,
    });
    const addLayout = (type) => setLayouts(prev => [...prev, blankLayout(type)]);
    const renameLayout = (id, description) => setLayouts(prev => prev.map(l => l.id === id ? { ...l, description } : l));
    const setLayoutVisible = (id, visible) => setLayouts(prev => prev.map(l => l.id === id ? { ...l, visible } : l));
    const removeLayout = (id) => setLayouts(prev => prev.filter(l => l.id !== id));
    const duplicateSystemDefault = (serverId) => {
        const src = layouts.find(l => l.serverId === serverId);
        if (!src) return;
        const name = `${(src.description || 'Layout').replace(/^\[Default\]\s*/i, '')} (copy)`.trim();
        setLayouts(prev => [...prev, blankLayout('DIGITAL', { description: name, copyFrom: serverId })]);
    };
    // Picking a file stashes the raw File (uploaded on save) + a blob preview.
    const setLayoutImageFile = (id, file) => setLayouts(prev => prev.map(l => l.id === id
        ? { ...l, imageFile: file || null, image: file ? { name: file.name, url: URL.createObjectURL(file) } : l.image } : l));
    const setLayoutPhysicalFile = (id, file) => setLayouts(prev => prev.map(l => l.id === id
        ? { ...l, layoutImageFile: file || null, layoutImage: file ? { name: file.name, url: URL.createObjectURL(file) } : l.layoutImage } : l));

    // Unlayer design editor (digital layouts). Opens a modal per layout; loads
    // the saved design via GET /cheques-layouts/{id}/design (or the in-session
    // edit), and on save stashes design+html + marks designDirty so the parent
    // save flow POSTs it to /cheques-layouts/{id}/layout.
    const [designerLayoutId, setDesignerLayoutId] = useState(null);
    const [designerInitialDesign, setDesignerInitialDesign] = useState(null);
    const [designerLoading, setDesignerLoading] = useState(false);
    const designerEditorRef = useRef(null);
    const designerLayout = layouts.find(l => l.id === designerLayoutId) || null;
    const openDesigner = async (id) => {
        const l = layouts.find(x => x.id === id);
        if (!l) return;
        const inSession = !!l.design;
        const willFetch = !inSession && l.serverId != null && !!api;
        // Batch: open the modal immediately, show a spinner while fetching.
        setDesignerInitialDesign(inSession ? l.design : null);
        setDesignerLoading(willFetch);
        setDesignerLayoutId(id);
        if (willFetch) {
            try {
                const r = await SftGlobalData.post('webshop&action=proxy', {
                    key: api, webshop_id: selectedWebshop?.id, sftUserId: login?.sft_user_id, user: login?.login,
                    method: 'GET', path: `/cheques-layouts/${l.serverId}/design`,
                });
                if (r?.success && r.data && r.data.design && typeof r.data.design === 'object') {
                    setDesignerInitialDesign(r.data.design);
                }
            } catch { /* start blank */ }
            setDesignerLoading(false);
        }
    };
    const closeDesigner = () => { setDesignerLayoutId(null); setDesignerInitialDesign(null); };
    const saveDesigner = () => {
        const editor = designerEditorRef.current?.editor;
        if (!editor) { closeDesigner(); return; }
        editor.saveDesign((design) => {
            editor.exportHtml((data) => {
                const html = (data && data.html) || '';
                setLayouts(prev => prev.map(l => l.id === designerLayoutId
                    ? { ...l, design, html, designDirty: true, designLoaded: true } : l));
                closeDesigner();
            });
        });
    };
    // Placeholders become Unlayer merge tags ({{PLC_*}}) so they can be inserted.
    const designerMergeTags = useMemo(() => {
        const tags = {};
        (Array.isArray(placeholdersProp) ? placeholdersProp : []).forEach((p) => {
            if (p.key) tags[p.key] = { name: p.description || p.key, value: `{{${p.key}}}` };
        });
        return tags;
    }, [placeholdersProp]);

    // TRANSLATIONS — driven by the backend cheque-placeholders rows
    // ({id, key, description, nl, en, fr}; new rows have no id). Controlled by
    // the parent when wired.
    const placeholdersControlled = Array.isArray(placeholdersProp) && typeof onPlaceholdersChange === 'function';
    const [localPlaceholders, setLocalPlaceholders] = useState([]);
    const placeholders = placeholdersControlled ? placeholdersProp : localPlaceholders;
    const setPlaceholders = placeholdersControlled ? onPlaceholdersChange : setLocalPlaceholders;
    const [translationsSearch, setTranslationsSearch] = useState('');

    const setPlaceholderLang = (idx, lang, val) => setPlaceholders(prev => prev.map((p, i) => i === idx ? { ...p, [lang]: val } : p));
    const removePlaceholder = (idx) => setPlaceholders(prev => prev.filter((_, i) => i !== idx));
    const [newCustomField, setNewCustomField] = useState('');
    const addCustomField = () => {
        const name = newCustomField.trim();
        if (!name) return;
        setPlaceholders(prev => [...prev, { id: null, key: '', description: name, nl: '', en: '', fr: '', _new: true }]);
        setNewCustomField('');
    };

    const visibleTranslationRows = useMemo(() => {
        const q = translationsSearch.trim().toLowerCase();
        const rows = placeholders.map((p, i) => ({ ...p, _idx: i }));
        if (!q) return rows;
        return rows.filter(r =>
            r.description?.toLowerCase().includes(q) ||
            r.key?.toLowerCase().includes(q) ||
            r.nl?.toLowerCase().includes(q) ||
            r.en?.toLowerCase().includes(q) ||
            r.fr?.toLowerCase().includes(q)
        );
    }, [placeholders, translationsSearch]);

    const SUBTABS = [
        { idx: 0, key: 'settings', icon: <SettingsIcon  fontSize="small" />, label: tr('cfg_voucher_subtab_settings', 'Settings') },
        { idx: 1, key: 'layouts',  icon: <GridViewIcon  fontSize="small" />, label: tr('cfg_voucher_subtab_layouts',  'Voucher layouts') },
        { idx: 2, key: 'trans',    icon: <TranslateIcon fontSize="small" />, label: tr('cfg_voucher_subtab_trans',    'Translations') },
    ];

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, gap: 3, alignItems: 'start' }}>
            {/* ─────────── Sidebar with sub-tabs ─────────── */}
            <Box>
                <Box sx={{ px: 1, mb: 1 }}>
                    <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                        {tr('cfg_voucher_sidebar_title', 'Gift voucher options')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {tr('cfg_voucher_sidebar_subtitle', 'Configure gift vouchers for your shop')}
                    </Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mx: 1, mb: 1 }} />
                <List dense disablePadding>
                    {SUBTABS.map((s) => {
                        const selected = voucherTab === s.idx;
                        return (
                            <ListItemButton
                                key={s.key}
                                selected={selected}
                                onClick={() => setVoucherTab(s.idx)}
                                sx={{
                                    borderRadius: 1.5, mb: 0.5, py: 1.25,
                                    '&.Mui-selected': { backgroundColor: 'rgba(25,118,210,0.08)' },
                                    '&.Mui-selected:hover': { backgroundColor: 'rgba(25,118,210,0.12)' },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32, color: selected ? 'primary.main' : 'text.secondary' }}>
                                    {s.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500, color: selected ? 'primary.main' : 'text.primary' }}>
                                            {s.label}
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>

            {/* ─────────── Detail content ─────────── */}
            <Box>
            {/* ─────────────────── SETTINGS ─────────────────── */}
            {voucherTab === 0 && (
                <Stack spacing={2}>
                    {/* Availability */}
                    <ConfigCard anchor="cfg-card-voucher-availability" title={tr('cfg_voucher_availability_title', 'Availability')} description={tr('cfg_voucher_availability_desc', 'Whether gift vouchers are sold in your shop, and what types are offered.')} theme={theme}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_show_nav', 'Show gift voucher in the navigation menu')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_show_nav_help', "Adds a 'Gift voucher' link to the main navigation so visitors can find it.")}</Typography>
                            </Box>
                            <Switch color="success" size="small" checked={config.availability.showInNavigation} onChange={(e) => setAvailability({ showInNavigation: e.target.checked })} />
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.25 }}>{tr('cfg_voucher_types', 'Available voucher types')}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{tr('cfg_voucher_types_help', 'Pick which kinds of gift vouchers customers can buy.')}</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                                <SelectableTile
                                    selected={config.availability.allowPhysical}
                                    onChange={(v) => setAvailability({ allowPhysical: v })}
                                    label={tr('cfg_voucher_physical', 'Physical voucher')}
                                    desc={tr('cfg_voucher_physical_desc', "Printed on paper and shipped to the customer's address.")}
                                />
                                <SelectableTile
                                    selected={config.availability.allowDigital}
                                    onChange={(v) => setAvailability({ allowDigital: v })}
                                    label={tr('cfg_voucher_digital', 'Digital voucher')}
                                    desc={tr('cfg_voucher_digital_desc', 'Sent by email — to the buyer or directly to a recipient.')}
                                />
                            </Box>
                        </Box>
                    </ConfigCard>

                    {/* Voucher amounts */}
                    <ConfigCard anchor="cfg-card-voucher-amounts" title={tr('cfg_voucher_amounts_title', 'Voucher amounts')} description={tr('cfg_voucher_amounts_desc', 'The amounts customers can choose when buying a gift voucher.')} theme={theme}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.25 }}>{tr('cfg_voucher_presets', 'Preset amounts')}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{tr('cfg_voucher_presets_help', 'Common amounts shown as quick choices. Type a number and click Add (or press Enter).')}</Typography>
                            <PresetAmountsInput
                                presets={config.amounts.presets}
                                onAdd={addPreset}
                                onRemove={removePreset}
                                addLabel={tr('cfg_voucher_add_amount', 'e.g. 75')}
                                addButtonLabel={tr('cfg_voucher_add_amount_btn', 'Add')}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_allow_custom', 'Allow custom amount')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_allow_custom_help', 'Customers can enter their own amount instead of picking a preset.')}</Typography>
                            </Box>
                            <Switch color="success" size="small" checked={config.amounts.allowCustom} onChange={(e) => setAmounts({ allowCustom: e.target.checked })} />
                        </Box>
                        {config.amounts.allowCustom && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pl: 2 }}>
                                <Box>
                                    <Typography variant="body2">{tr('cfg_voucher_amount_range', 'Amount range')}</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_amount_range_help', 'Minimum and maximum amounts a customer can enter.')}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        type="number" size="small"
                                        value={config.amounts.customMin}
                                        onChange={(e) => setAmounts({ customMin: Math.max(0, parseInt(e.target.value) || 0) })}
                                        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                        sx={{ width: 100 }}
                                    />
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_to', 'to')}</Typography>
                                    <TextField
                                        type="number" size="small"
                                        value={config.amounts.customMax}
                                        onChange={(e) => setAmounts({ customMax: Math.max(0, parseInt(e.target.value) || 0) })}
                                        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                        sx={{ width: 100 }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </ConfigCard>

                    {/* Customer discounts */}
                    <ConfigCard anchor="cfg-card-voucher-discounts" title={tr('cfg_voucher_discounts_title', 'Customer discounts')} description={tr('cfg_voucher_discounts_desc', 'How loyalty discounts apply to gift voucher purchases.')} theme={theme}>
                        <RowToggle
                            label={tr('cfg_voucher_disc_count', 'Count voucher purchases toward customer discount totals')}
                            help={tr('cfg_voucher_disc_count_help', 'When on, money spent on gift vouchers contributes to the customer\'s loyalty progress.')}
                            checked={config.discounts.countTowardTotals}
                            onChange={(v) => setDiscounts({ countTowardTotals: v })}
                        />
                        <RowToggle
                            label={tr('cfg_voucher_disc_voucher_only', 'Apply customer discount to voucher-only orders')}
                            help={tr('cfg_voucher_disc_voucher_only_help', 'When on, customers can use their loyalty discount on orders containing only gift vouchers.')}
                            checked={config.discounts.applyToVoucherOnly}
                            onChange={(v) => setDiscounts({ applyToVoucherOnly: v })}
                        />
                        <RowToggle
                            label={tr('cfg_voucher_disc_mixed', 'Apply customer discount to mixed orders')}
                            help={tr('cfg_voucher_disc_mixed_help', 'When on, the loyalty discount applies to orders that contain both vouchers and regular products.')}
                            checked={config.discounts.applyToMixedOrders}
                            onChange={(v) => setDiscounts({ applyToMixedOrders: v })}
                        />
                    </ConfigCard>

                    {/* Shipping */}
                    <ConfigCard anchor="cfg-card-voucher-shipping" title={tr('cfg_voucher_shipping_title', 'Shipping (physical vouchers)')} description={tr('cfg_voucher_shipping_desc', 'Cost of sending physical gift vouchers to the customer.')} theme={theme}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_ship_cost', 'Shipping cost')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_ship_cost_help', 'Charged on each order with a physical voucher.')}</Typography>
                            </Box>
                            <TextField
                                type="number" size="small"
                                value={config.shipping.cost}
                                onChange={(e) => setShipping({ cost: Math.max(0, parseFloat(e.target.value) || 0) })}
                                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                sx={{ width: 110 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_free_threshold', 'Free shipping from')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_free_threshold_help', 'Orders from this amount up get free shipping. Set to 0 to disable.')}</Typography>
                            </Box>
                            <TextField
                                type="number" size="small"
                                value={config.shipping.freeFrom}
                                onChange={(e) => setShipping({ freeFrom: Math.max(0, parseFloat(e.target.value) || 0) })}
                                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                sx={{ width: 110 }}
                            />
                        </Box>
                    </ConfigCard>

                    {/* Personalization */}
                    <ConfigCard anchor="cfg-card-voucher-personalization" title={tr('cfg_voucher_pers_title', 'Personalization')} description={tr('cfg_voucher_pers_desc', 'Let customers add sender/recipient details and a personal message to the voucher.')} theme={theme}>
                        {/* Sender & recipient info (CHEQUES_ALLOW_INFO_ON_TYPE) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_pers_info', 'Sender & recipient details')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_pers_info_help', 'For which voucher types the customer can fill in sender and recipient details.')}</Typography>
                            </Box>
                            <TextField
                                select size="small"
                                value={config.personalization.infoOnType}
                                onChange={(e) => setPersonalization({ infoOnType: e.target.value })}
                                sx={{ minWidth: 220 }}
                            >
                                <MenuItem value="NONE">{tr('cfg_voucher_type_none', 'None')}</MenuItem>
                                <MenuItem value="PHYSICAL">{tr('cfg_voucher_type_physical', 'Physical')}</MenuItem>
                                <MenuItem value="DIGITAL">{tr('cfg_voucher_type_digital', 'Digital')}</MenuItem>
                                <MenuItem value="BOTH">{tr('cfg_voucher_type_both', 'Both')}</MenuItem>
                            </TextField>
                        </Box>
                        {/* Send digital voucher straight to the recipient (CHEQUES_ALLOW_SEND_TO_RECEIVER) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, opacity: sendToReceiverDisabled ? 0.5 : 1 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_pers_send_direct', 'Send digital voucher directly to recipient')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_pers_send_direct_help', "Lets the customer enter the recipient's email so the voucher arrives in their inbox, not the buyer's. Requires digital vouchers with sender/recipient details.")}</Typography>
                            </Box>
                            <Switch color="success" size="small" disabled={sendToReceiverDisabled} checked={config.personalization.sendDigitalDirectly} onChange={(e) => setPersonalization({ sendDigitalDirectly: e.target.checked })} />
                        </Box>
                        {/* Personal message (CHEQUES_ALLOW_MESSAGE_ON_TYPE) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_pers_message', 'Personal message')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_pers_message_help', 'For which voucher types the customer can add a short message for the recipient.')}</Typography>
                            </Box>
                            <TextField
                                select size="small"
                                value={config.personalization.messageOnType}
                                onChange={(e) => setPersonalization({ messageOnType: e.target.value })}
                                sx={{ minWidth: 220 }}
                            >
                                <MenuItem value="NONE">{tr('cfg_voucher_type_none', 'None')}</MenuItem>
                                <MenuItem value="PHYSICAL">{tr('cfg_voucher_type_physical', 'Physical')}</MenuItem>
                                <MenuItem value="DIGITAL">{tr('cfg_voucher_type_digital', 'Digital')}</MenuItem>
                                <MenuItem value="BOTH">{tr('cfg_voucher_type_both', 'Both')}</MenuItem>
                            </TextField>
                        </Box>
                    </ConfigCard>
                </Stack>
            )}

            {/* ─────────────────── VOUCHER LAYOUTS ─────────────────── */}
            {voucherTab === 1 && (
                <Stack spacing={2}>
                    <LayoutGroup
                        anchor="cfg-card-voucher-layouts-physical"
                        title={tr('cfg_voucher_layouts_physical', 'Physical voucher layouts')}
                        desc={tr('cfg_voucher_layouts_physical_desc', 'Designs customers can pick from when buying a printed voucher.')}
                        kind="physical"
                        layouts={physicalLayouts}
                        onAdd={() => addLayout('PHYSICAL')}
                        onRename={renameLayout}
                        onVisible={setLayoutVisible}
                        onRemove={removeLayout}
                        onPreviewImage={setLayoutImageFile}
                        onPhysicalImage={setLayoutPhysicalFile}
                        addLabel={tr('cfg_voucher_layouts_new', 'New layout')}
                        tr={tr} theme={theme}
                    />
                    <LayoutGroup
                        anchor="cfg-card-voucher-layouts-digital"
                        title={tr('cfg_voucher_layouts_digital', 'Digital voucher layouts')}
                        desc={tr('cfg_voucher_layouts_digital_desc', 'Designs customers can pick from when buying an emailed voucher.')}
                        kind="digital"
                        layouts={digitalLayouts}
                        onAdd={() => addLayout('DIGITAL')}
                        onRename={renameLayout}
                        onVisible={setLayoutVisible}
                        onRemove={removeLayout}
                        onPreviewImage={setLayoutImageFile}
                        onEditDesign={openDesigner}
                        addLabel={tr('cfg_voucher_layouts_new', 'New layout')}
                        tr={tr} theme={theme}
                    />
                    <ConfigCard anchor="cfg-card-voucher-layouts-defaults" title={tr('cfg_voucher_layouts_defaults', 'System defaults')} description={tr('cfg_voucher_layouts_defaults_desc', 'Built-in templates that ship with the platform. These can be duplicated to customize, but not deleted.')} theme={theme}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
                            {systemDefaults.length === 0 && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', gridColumn: '1 / -1', py: 1 }}>
                                    {tr('cfg_voucher_layouts_defaults_empty', 'No built-in templates found for this shop.')}
                                </Typography>
                            )}
                            {systemDefaults.map((s) => (
                                <Paper key={s.id} variant="outlined" sx={{
                                    position: 'relative',
                                    aspectRatio: '4 / 3',
                                    backgroundColor: '#000', color: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                    '&:hover .sys-dup': { opacity: 1 },
                                }}>
                                    {s.image?.url
                                        ? <Box component="img" src={s.image.url} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                        : <Box sx={{ fontWeight: 700, fontSize: '1.1rem' }}>LOGO</Box>}
                                    <Box sx={{ position: 'absolute', top: 4, left: 4, fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', letterSpacing: 0.5, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                                        {tr('cfg_voucher_layouts_default_badge', 'DEFAULT')}
                                    </Box>
                                    <IconButton
                                        className="sys-dup"
                                        size="small"
                                        onClick={() => duplicateSystemDefault(s.serverId)}
                                        title={tr('cfg_voucher_layouts_duplicate', 'Duplicate to digital layouts')}
                                        sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, color: '#fff', backgroundColor: 'rgba(0,0,0,0.4)' }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                    <Box sx={{ position: 'absolute', bottom: 4, left: 4, right: 4, fontSize: '0.65rem', color: '#fff', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                                        {s.description}
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </ConfigCard>
                </Stack>
            )}

            {/* ─────────────────── TRANSLATIONS ─────────────────── */}
            {voucherTab === 2 && (
                <ConfigCard
                    anchor="cfg-card-voucher-translations"
                    title={tr('cfg_voucher_trans_title', 'Translations')}
                    description={tr('cfg_voucher_trans_desc', 'Labels shown on vouchers in each language. Edit the translations directly in the table below.')}
                    headerActions={
                        <TextField
                            size="small"
                            placeholder={tr('cfg_voucher_trans_search', 'Search translations…')}
                            value={translationsSearch}
                            onChange={(e) => setTranslationsSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            sx={{ minWidth: 240 }}
                        />
                    }
                    theme={theme}
                >
                    <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}>{tr('cfg_voucher_trans_field',  'FIELD')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}>{tr('cfg_voucher_trans_dutch',  'DUTCH (NL)')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}>{tr('cfg_voucher_trans_english','ENGLISH (EN)')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}>{tr('cfg_voucher_trans_french', 'FRENCH (FR)')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }} align="right">{tr('cfg_voucher_trans_actions','ACTIONS')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleTranslationRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', py: 1.5, textAlign: 'center' }}>
                                                {tr('cfg_voucher_trans_empty', 'No translations yet — add a custom field below.')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {visibleTranslationRows.map((row) => {
                                    const idx = row._idx;
                                    const onLangChange = (lang) => (e) => setPlaceholderLang(idx, lang, e.target.value);
                                    // Seeded system placeholders are referenced by the
                                    // default voucher templates — editable, not deletable.
                                    const isSystemRow = SYSTEM_PLACEHOLDER_KEYS.includes(row.key);
                                    return (
                                        <TableRow key={row.id ?? `new-${idx}`} hover>
                                            <TableCell sx={{ verticalAlign: 'top', minWidth: 180 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{row.description}</Typography>
                                                    {row._new && (
                                                        <Chip
                                                            label={tr('cfg_voucher_trans_new', 'NEW')}
                                                            size="small" color="primary" variant="filled"
                                                            sx={{ fontSize: '0.55rem', height: 16, '& .MuiChip-label': { px: 0.75 } }}
                                                        />
                                                    )}
                                                    {isSystemRow && (
                                                        <Chip
                                                            label={tr('cfg_voucher_trans_standard', 'STANDARD')}
                                                            size="small" variant="outlined"
                                                            sx={{ fontSize: '0.55rem', height: 16, '& .MuiChip-label': { px: 0.75 } }}
                                                        />
                                                    )}
                                                </Box>
                                                {row.key ? (
                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.65rem', mt: 0.25 }}>
                                                        {row.key}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', fontSize: '0.65rem', mt: 0.25 }}>
                                                        {tr('cfg_voucher_trans_key_pending', 'key generated on save')}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <TextField size="small" fullWidth value={row.nl || ''} onChange={onLangChange('nl')} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} />
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <TextField size="small" fullWidth value={row.en || ''} onChange={onLangChange('en')} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} />
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <TextField size="small" fullWidth value={row.fr || ''} onChange={onLangChange('fr')} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} />
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }} align="right">
                                                <span title={isSystemRow
                                                    ? tr('cfg_voucher_trans_system_locked', 'System field — used by the default voucher templates. Editing is allowed, deleting is not.')
                                                    : tr('cfg_voucher_trans_remove', 'Remove field')}>
                                                    <IconButton size="small" color="error" disabled={isSystemRow} onClick={() => removePlaceholder(idx)}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>

                    {/* Add custom translation row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                        <AddIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <TextField
                            size="small" fullWidth
                            placeholder={tr('cfg_voucher_trans_add_placeholder', 'Custom field name (e.g. Special offer)')}
                            value={newCustomField}
                            onChange={(e) => setNewCustomField(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomField(); } }}
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                        />
                        <Button variant="contained" size="small" onClick={addCustomField} disabled={!newCustomField.trim()} sx={{ flexShrink: 0, textTransform: 'none' }}>
                            {tr('cfg_voucher_trans_add', 'Add custom translation')}
                        </Button>
                    </Box>

                    {/* The former read-only "standard labels" reference table is
                        gone — every FLD_* mockup label turned out to map onto a
                        seeded PLC_* row that is already editable above (see the
                        SYSTEM_PLACEHOLDER_KEYS note at the top of this file). */}
                </ConfigCard>
            )}

            {/* ─────────────── Digital layout design editor (Unlayer) ─────────────── */}
            {/* Full-height Unlayer editor: shared "wide" modal tier + a fixed height. */}
            <Dialog open={designerLayoutId != null} onClose={closeDesigner}
                {...webshopModalSizing('wide', { sx: { height: '90vh' } })}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.25 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {tr('cfg_voucher_designer_title', 'Edit voucher design')}{designerLayout ? ` — ${designerLayout.description}` : ''}
                    </Typography>
                    <IconButton size="small" onClick={closeDesigner}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                    {designerLoading ? (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360 }}>
                            <CircularProgress />
                        </Box>
                    ) : designerLayoutId != null ? (
                        <UnlayerEditor
                            ref={designerEditorRef}
                            minHeight="100%"
                            displayMode="email"
                            options={{ mergeTags: designerMergeTags }}
                            onReady={(editor) => {
                                if (designerInitialDesign) {
                                    try { editor.loadDesign(designerInitialDesign); } catch { /* blank */ }
                                }
                            }}
                            style={{ flex: 1 }}
                        />
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDesigner} sx={{ textTransform: 'none' }}>
                        {tr('cfg_voucher_designer_cancel', 'Cancel')}
                    </Button>
                    <Button variant="contained" onClick={saveDesigner} sx={{ textTransform: 'none' }}>
                        {tr('cfg_voucher_designer_save', 'Apply design')}
                    </Button>
                </DialogActions>
            </Dialog>
            </Box>
        </Box>
    );
};

// ---------- helpers ----------

const SelectableTile = ({ selected, onChange, label, desc }) => (
    <Paper
        variant="outlined"
        onClick={() => onChange(!selected)}
        sx={{
            p: 1.5,
            display: 'flex', alignItems: 'flex-start', gap: 1,
            cursor: 'pointer',
            borderColor: selected ? 'primary.main' : 'divider',
            backgroundColor: selected ? 'rgba(25,118,210,0.06)' : 'transparent',
            borderWidth: selected ? 2 : 1,
            transition: 'all 120ms',
            borderRadius: 1.5,
        }}
    >
        <Checkbox size="small" checked={selected} onChange={(e) => onChange(e.target.checked)} sx={{ p: 0.25, mt: -0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}>{desc}</Typography>
        </Box>
    </Paper>
);

const PresetAmountsInput = ({ presets, onAdd, onRemove, addLabel, addButtonLabel }) => {
    const [val, setVal] = useState('');
    const canAdd = String(val).trim() !== '' && Number(val) > 0;
    const submit = () => { if (canAdd) { onAdd(val); setVal(''); } };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {presets.map((p) => (
                <Chip
                    key={p}
                    label={`€${p}`}
                    size="small"
                    onDelete={() => onRemove(p)}
                    deleteIcon={<CloseIcon fontSize="small" />}
                    sx={{ fontWeight: 600 }}
                />
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                    type="number" size="small"
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                    placeholder={addLabel}
                    InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                    sx={{ width: 110, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }}
                />
                <Button
                    variant="contained" size="small" onClick={submit} disabled={!canAdd}
                    startIcon={<AddIcon />}
                    sx={{ flexShrink: 0, textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                    {addButtonLabel}
                </Button>
            </Box>
        </Box>
    );
};

const RowToggle = ({ label, help, checked, onChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
            <Typography variant="body2">{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{help}</Typography>}
        </Box>
        <Switch color="success" size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </Box>
);

const LayoutGroup = ({ anchor, title, desc, kind, layouts, onAdd, onRename, onVisible, onRemove, onPreviewImage, onPhysicalImage, onEditDesign, addLabel, tr, theme }) => (
    <ConfigCard anchor={anchor} title={title} description={desc} theme={theme}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5 }}>
            {/* "+ New layout" tile */}
            <Paper variant="outlined" sx={{
                aspectRatio: '4 / 3', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                borderStyle: 'dashed', borderRadius: 1.5,
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
            }} onClick={onAdd}>
                <AddIcon fontSize="small" />
                <Typography variant="caption">{addLabel}</Typography>
            </Paper>

            {layouts.map((l) => (
                <Box key={l.id}>
                    {/* Preview thumbnail — click to upload the preview image. */}
                    <Box component="label" sx={{
                        position: 'relative', display: 'block',
                        aspectRatio: '4 / 3',
                        backgroundColor: '#000', color: '#fff',
                        borderRadius: 1.5, overflow: 'hidden', cursor: 'pointer',
                        border: '1px solid', borderColor: 'divider',
                        '&:hover .lay-rm, &:hover .lay-ov': { opacity: 1 },
                    }}>
                        {l.image?.url
                            ? <Box component="img" src={l.image.url} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>LOGO</Box>}
                        <Box className="lay-ov" sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.45)', color: '#fff', opacity: 0, transition: 'opacity 0.15s', fontSize: '0.7rem', fontWeight: 600, gap: 0.5 }}>
                            <AddIcon fontSize="small" /> {tr('cfg_voucher_layouts_set_preview', 'Preview image')}
                        </Box>
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) onPreviewImage(l.id, f); e.target.value = ''; }} />
                        {!l.isSystem && (
                            <IconButton
                                className="lay-rm"
                                size="small"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(l.id); }}
                                sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, color: '#fff', backgroundColor: 'rgba(0,0,0,0.4)' }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    <TextField
                        size="small" variant="standard" fullWidth
                        value={l.description}
                        onChange={(e) => onRename(l.id, e.target.value)}
                        sx={{ mt: 0.5, '& .MuiInput-input': { fontSize: '0.78rem', py: 0.25 } }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}>
                        <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                            <Switch size="small" color="success" checked={!!l.visible} onChange={(e) => onVisible(l.id, e.target.checked)} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_layouts_visible', 'Visible')}</Typography>
                        </Box>
                        {kind === 'physical' && (
                            <>
                                {/* A real <button> that clicks a detached hidden
                                    input — component="label" made MUI render a
                                    <label>, which the admin's global CSS styles
                                    differently from the "Edit design" button. */}
                                <Button size="small"
                                    onClick={() => document.getElementById(`lay-file-${l.id}`)?.click()}
                                    sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0 }}>
                                    {l.layoutImage ? tr('cfg_voucher_layouts_replace_file', 'Replace file') : tr('cfg_voucher_layouts_set_file', 'Layout image')}
                                </Button>
                                <input id={`lay-file-${l.id}`} type="file" accept="image/*" style={{ display: 'none' }}
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) onPhysicalImage(l.id, f); e.target.value = ''; }} />
                            </>
                        )}
                        {kind === 'digital' && typeof onEditDesign === 'function' && (
                            <Button size="small" onClick={() => onEditDesign(l.id)} sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0 }}>
                                {tr('cfg_voucher_layouts_edit_design', 'Edit design')}
                            </Button>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    </ConfigCard>
);

CadeaubonTab.propTypes = {
    theme: PropTypes.string,
    t: PropTypes.func,
    config: PropTypes.object,
    onConfigChange: PropTypes.func,
    placeholders: PropTypes.array,
    onPlaceholdersChange: PropTypes.func,
    layouts: PropTypes.array,
    onLayoutsChange: PropTypes.func,
    login: PropTypes.object,
    api: PropTypes.string,
    selectedWebshop: PropTypes.object,
};
LayoutGroup.propTypes = {
    anchor: PropTypes.string, title: PropTypes.string, desc: PropTypes.string, kind: PropTypes.string, layouts: PropTypes.array,
    onAdd: PropTypes.func, onRename: PropTypes.func, onVisible: PropTypes.func, onRemove: PropTypes.func,
    onPreviewImage: PropTypes.func, onPhysicalImage: PropTypes.func, onEditDesign: PropTypes.func,
    addLabel: PropTypes.string, tr: PropTypes.func, theme: PropTypes.string,
};
export default CadeaubonTab;
